import { Stack, TextField } from "@mui/material";
import React, { ReactElement, useEffect, useRef } from "react";

interface CodeInputProps {
  onDone: (code: string) => void;
  digits?: number;
}

const CodeInput: React.FC<CodeInputProps> = ({
  onDone,
  digits = 6,
}): ReactElement => {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    const value = e.target.value;
    if (value.length > 1) {
      return;
    }

    if (value && index < digits - 1) {
      inputsRef.current[index + 1]?.focus();
    }

    if (index === digits - 1 && value) {
      const code = inputsRef.current
        .map((input) => input?.value || "")
        .join("");

      onDone(code);

      clearInputs();
    }
  };

  const clearInputs = () => {
    setTimeout(() => {
      inputsRef.current.forEach((input) => {
        if (input) {
          input.value = "";
        }
      });
      inputsRef.current[0]?.focus();
    }, 400);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData
      .getData("Text")
      .replace(/[^0-9]/g, "") 
      .slice(0, digits)
      .split("");

    pasteData.forEach((char, idx) => {
      const input = inputsRef.current[idx];
      if (input) {
        input.value = char;
      }
    });

    if (pasteData.length === digits) {
      onDone(pasteData.join(""));
      clearInputs();
    } else {
      inputsRef.current[pasteData.length]?.focus();
    }
  };

  return (
    <Stack
      width={"100%"}
      direction={"row"}
      justifyContent={"space-between"}
      alignItems={"center"}
      gap={2}
      onPaste={handlePaste}
    >
      {Array.from({ length: digits }).map((_, index) => (
        <TextField
          sx={{ width: "auto", input: { textAlign: "center" } }}
          key={index}
          variant="outlined"
          required
          type="number"
          inputRef={(el) => (inputsRef.current[index] = el)}
          slotProps={{
            htmlInput: {
              textAlign: "center",
            },
          }}
          onFocus={(e) => {
            e.target.value = "";
          }}
          fullWidth
          autoFocus={index === 0}
          onChange={(e) => handleChange(e, index)}
        />
      ))}
    </Stack>
  );
};

export default CodeInput;
